import { useMutation, useQuery } from "@apollo/client/react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { ButtonDots, itemClasses } from "../../../../../components/appearance";
import { ErrorFallback, Head } from "../../../../../components/core";
import {
  Button,
  FieldFleetVendor,
  FieldVehicleExpenseType,
} from "../../../../../components/form";
import { LimitBy, TableCursor } from "../../../../../components/table";
import {
  GET_EXPENSEENTRIES,
  REMOVE_EXPENSEENTRY,
} from "../../../../../graphql/fleets/vehicles/pages/expense-entry";
import { VehicleExpenseEntry } from "../../../../../graphql/fleets/vehicles/pages/expense-entry";
import { AlertModal, AlertType, useAlert } from "../../../../../hooks/useAlert";
import { classNames, formatCurrency, formatDate } from "../../../../../utils";
import { AuthShield } from "../../../../auth/core";

const ExpenseEntryList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { t } = useTranslation();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery<{
    fetchExpenseEntries: VehicleExpenseEntry[] | null;
  }>(GET_EXPENSEENTRIES);

  const expenseEntries = useMemo<VehicleExpenseEntry[]>(() => {
    const updatedExpenseEntry = data?.fetchExpenseEntries?.length
      ? data?.fetchExpenseEntries.map((item) => {
          return {
            ...item,
            expenseDate: formatDate(item.expenseDate),
            createdAt: formatDate(item.createdAt),
          };
        })
      : [];
    return updatedExpenseEntry;
  }, [data]);

  const [deleteVehicleExpenseEntry] = useMutation(REMOVE_EXPENSEENTRY);
  const handleDelete = async (id: string) => {
    deleteVehicleExpenseEntry({
      variables: {
        id: Number(id),
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleExpenseEntryDelete) {
          await refetch();
          toast.success(`Vehicle expense entry deleted successfully`);
        } else {
          toast.error("Something went wrong, please try again later");
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<VehicleExpenseEntry, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "expenseDate",
        header: "Expense Date",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.expenseDate,
      },
      {
        accessorKey: "vehicleExpenseTypeId",
        header: "Expense Type",
        cell: (props) => (
          <FieldVehicleExpenseType
            title="Expense Type"
            value={props.row.original.vehicleExpenseTypeId}
            isPreview
          />
        ),
      },
      {
        accessorKey: "notes",
        header: "Notes",
        size: 300,
        enableHiding: false,
      },
      {
        accessorKey: "vendor",
        header: "Vendor",
        cell: (props) => (
          <FieldFleetVendor
            title="Vendor"
            value={props.row.original.fleetVendorId}
            isPreview
          />
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">
            {formatCurrency(props.row.original.amount ?? 0)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <ButtonDots>
            <AuthShield access={["read-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/expense-entries/${props.row.original.id}`}
              >
                <div className="flex items-center">
                  <EyeIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    View
                    <span className="sr-only">
                      , {props.row.original.expenseDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["update-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/expense-entries/${props.row.original.id}/edit`}
              >
                <div className="flex items-center">
                  <PencilIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Edit
                    <span className="sr-only">
                      , {props.row.original.expenseDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                onClick={() => {
                  setAlert({
                    id: props.row.original.id!,
                    name: props.row.original.expenseDate ?? "",
                  });
                }}
              >
                <div className="flex items-center">
                  <TrashIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Delete
                    <span className="sr-only">
                      , {props.row.original.expenseDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
          </ButtonDots>
        ),
      },
    ],
    []
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={ExpenseEntryListResource.name}
        heading={ExpenseEntryListResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: ExpenseEntryListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-6 sm:flex sm:items-center md:mb-8">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {t("heading_vehicle_expense_entry")}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {t("description_vehicle_expense_entry")}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-vehicles"]}>
              <Button type="link" href="/fleets/vehicles/expense-entries/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add expense entry
              </Button>
            </AuthShield>
            <Button
              variant="icon"
              onClick={handleRefetch}
              disabled={loading}
              className="flex h-9 items-center justify-center px-2 text-blue-700"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={classNames(
                  "h-5 w-5",
                  refetching ? "animate-spin" : ""
                )}
              />
              <span className="sr-only">Refresh expense entry list</span>
            </Button>
          </div>
        </div>

        <TableCursor
          data={expenseEntries}
          columns={columns}
          loading={loading}
          totalRows={expenseEntries.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          enableRowSelection={false}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No expense entries found"
          fallbackText="You can add a new expense entry by clicking on add expense entry button."
        />
      </div>
    </>
  );
};

export default ExpenseEntryList;
export const ExpenseEntryListResource: ResourceProps = {
  name: "Expense Entries",
  description: "Update an existing vehicle expense entries",
  access: ["update-vehicles"],
  path: "/expense-entries",
  icon: "bi bi-person",
};
